import React, { useState } from 'react';
import { Runbook } from '../types';

interface CompilerViewProps {
  onPublishToCatalog: (runbook: Runbook) => void;
  onOpenReader: (slug: string) => void;
}

export const CompilerView: React.FC<CompilerViewProps> = ({
  onPublishToCatalog,
  onOpenReader,
}) => {
  const [selectedCat, setSelectedCat] = useState<'pricing' | 'operations' | 'sales' | 'launch'>('pricing');
  const [niche, setNiche] = useState('');
  const [price, setPrice] = useState('24');
  const [compiledRunbook, setCompiledRunbook] = useState<Runbook | null>(null);

  const compileRunbook = () => {
    const domain = niche.trim() || 'Full-Stack Tech Debt Audit';
    const numPrice = Number(price) || 24;
    const slug = domain.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-runbook';
    const title = `The ${domain} Runbook`;
    const subtitle = `Structured execution protocol for ${domain.toLowerCase()}.`;
    const code = `RB-${Math.floor(10 + Math.random() * 90)}`;

    const newRunbook: Runbook = {
      id: `rb-comp-${Date.now()}`,
      slug,
      code,
      title,
      subtitle,
      category: selectedCat,
      niche: domain,
      price: numPrice,
      blurb: `An industrial-grade operating protocol for delivering ${domain.toLowerCase()} without meeting sprawl or scope creep.`,
      promise: `Package ${domain.toLowerCase()} into a predictable, high-value commercial deliverable in one sitting.`,
      audience: `Specialists and consultants executing in ${domain.toLowerCase()}.`,
      isCustom: true,
      bullets: [
        'The 4-phase technical execution roadmap',
        'Client boundary defense scripts & intake specification',
        'Commercial quotation one-pager with deposit milestones',
        'Complete exportable markdown & print-ready PDF layout',
      ],
      sections: [
        {
          heading: `01 · Foundational Scoping for ${domain}`,
          body: `Most operators undercharge for ${domain.toLowerCase()} because they define scope loosely. Lock down the exact boundaries before signing.\n\nNever accept undefined exploratory goals. Define what 'Done' looks like in measurable system terms.`,
        },
        {
          heading: '02 · The Production Template',
          body: `Phase 1: Diagnostic telemetry & asset intake.\nPhase 2: Core technical execution sprint (48 hours).\nPhase 3: Formal findings memo and async video walkthrough.\nPhase 4: Client sign-off and milestone balance settlement.`,
        },
        {
          heading: '03 · Scope Defense Scripts',
          body: `When asked for extra features during the sprint:\n'That is a valuable improvement. It sits outside this protocol's defined scope, so I can schedule an add-on sprint next week to implement it cleanly.'`,
        },
      ],
      faqs: [
        {
          q: 'Can I modify this protocol?',
          a: 'Yes, this runbook is yours to adapt, rebrand, and deploy for client deliverables.',
        },
      ],
      emails: [
        {
          subject: `Protocol Initialized: ${title}`,
          body: `Your execution files for ${domain.toLowerCase()} are ready. Open section 2 and fill the bracketed variables.`,
        },
      ],
      posts: [
        `Shipped ${title}. The complete blueprint for handling ${domain.toLowerCase()} without another 45-minute sync.`,
      ],
    };

    setCompiledRunbook(newRunbook);
  };

  const handlePublish = () => {
    if (!compiledRunbook) return;
    onPublishToCatalog(compiledRunbook);
    alert(`Published "${compiledRunbook.title}" to catalog!`);
  };

  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6">
          <div>
            <p className="text-xs uppercase font-mono tracking-widest text-sky-400">
              THE COMPILER // GENERATIVE ENGINE
            </p>
            <h1 className="font-tech text-3xl font-bold text-white mt-1">
              Compile New Runbook
            </h1>
            <p className="text-xs text-[#8b92a5] font-mono mt-2">
              Generate an original execution runbook with structured templates, objection defenses, and launch copy for any specific problem domain.
            </p>
          </div>

          {/* Category Selector */}
          <div className="space-y-2">
            <label className="text-xs font-mono uppercase text-[#8b92a5]">
              Select Blueprint Architecture
            </label>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => setSelectedCat('pricing')}
                className={`p-3 rounded-lg text-left border transition ${
                  selectedCat === 'pricing'
                    ? 'border-sky-500/50 bg-[#181b26]'
                    : 'border-[#232736] bg-[#10121a]'
                }`}
              >
                <span className="block text-xs font-tech font-bold text-white uppercase">
                  Sprint Pricing Matrix
                </span>
                <span className="block text-[11px] font-mono text-[#8b92a5]">
                  Value tiering, rate cards, and pushback defenses.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCat('operations')}
                className={`p-3 rounded-lg text-left border transition ${
                  selectedCat === 'operations'
                    ? 'border-sky-500/50 bg-[#181b26]'
                    : 'border-[#232736] bg-[#10121a]'
                }`}
              >
                <span className="block text-xs font-tech font-bold text-white uppercase">
                  Async Operating Protocol
                </span>
                <span className="block text-[11px] font-mono text-[#8b92a5]">
                  Zero-meeting client intake, ticket queues, and handoffs.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCat('sales')}
                className={`p-3 rounded-lg text-left border transition ${
                  selectedCat === 'sales'
                    ? 'border-sky-500/50 bg-[#181b26]'
                    : 'border-[#232736] bg-[#10121a]'
                }`}
              >
                <span className="block text-xs font-tech font-bold text-white uppercase">
                  Deal Closer Swipe Engine
                </span>
                <span className="block text-[11px] font-mono text-[#8b92a5]">
                  Escalation ladder for stalled proposals and payment freeze.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCat('launch')}
                className={`p-3 rounded-lg text-left border transition ${
                  selectedCat === 'launch'
                    ? 'border-sky-500/50 bg-[#181b26]'
                    : 'border-[#232736] bg-[#10121a]'
                }`}
              >
                <span className="block text-xs font-tech font-bold text-white uppercase">
                  36-Hour Launch Runbook
                </span>
                <span className="block text-[11px] font-mono text-[#8b92a5]">
                  Timeboxed weekend launch plan for micro-products.
                </span>
              </button>
            </div>
          </div>

          {/* Compiler Inputs */}
          <div className="space-y-4 pt-2">
            <div className="space-y-1">
              <label className="text-xs font-mono uppercase text-[#8b92a5]">
                Niche / Domain
              </label>
              <input
                type="text"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="e.g. Next.js Performance Optimization Audit"
                className="w-full px-3 py-2 rounded bg-[#181b26] border border-[#232736] text-xs font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-sky-400"
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                <button
                  type="button"
                  onClick={() => setNiche('Fractional CTO Architecture Sprints')}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-[#181b26] text-zinc-400 hover:text-white"
                >
                  CTO Sprints
                </button>
                <button
                  type="button"
                  onClick={() => setNiche('B2B Copywriting Teardown Retainer')}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-[#181b26] text-zinc-400 hover:text-white"
                >
                  Copy Retainer
                </button>
                <button
                  type="button"
                  onClick={() => setNiche('Design System Sprint Audit')}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-[#181b26] text-zinc-400 hover:text-white"
                >
                  Design Audit
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-mono uppercase text-[#8b92a5]">
                  Price ($ USD)
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#181b26] border border-[#232736] text-xs font-mono text-white focus:outline-none focus:border-sky-400"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-mono uppercase text-[#8b92a5]">
                  Action
                </label>
                <button
                  onClick={compileRunbook}
                  className="w-full py-2 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-xs rounded transition"
                >
                  EXECUTE COMPILER
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Output */}
        <div className="lg:col-span-7">
          {!compiledRunbook ? (
            <div className="h-96 rounded-xl border border-[#232736] bg-[#10121a] p-8 flex flex-col justify-between grid-bg">
              <span className="text-xs font-mono text-sky-400 uppercase">SYS // IDLE</span>
              <div className="space-y-2 max-w-md">
                <h3 className="font-tech text-2xl font-bold text-white">
                  Select a blueprint architecture and define a domain.
                </h3>
                <p className="text-xs font-mono text-[#8b92a5]">
                  The Compiler constructs an un-redacted operational runbook with execution templates, client defense scripts, and distribution copy.
                </p>
              </div>
              <span className="text-[10px] font-mono text-[#525970]">
                READY ON STACK: CLOUDFLARE EDGE
              </span>
            </div>
          ) : (
            <div className="blueprint-card rounded-xl p-6 border border-sky-500/40 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono text-sky-400 uppercase">
                  COMPILATION SUCCESSFUL
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePublish}
                    className="px-3 py-1 bg-white text-black font-tech font-bold text-xs rounded hover:bg-zinc-200 transition"
                  >
                    ADD TO STORE
                  </button>
                  <button
                    onClick={() => onOpenReader(compiledRunbook.slug)}
                    className="px-3 py-1 bg-[#181b26] border border-[#232736] text-xs font-mono text-white rounded hover:bg-zinc-800 transition"
                  >
                    READ &rarr;
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-tech text-2xl font-bold text-white">
                  {compiledRunbook.title}
                </h3>
                <p className="text-xs font-mono text-[#8b92a5] italic mt-1">
                  {compiledRunbook.subtitle}
                </p>
              </div>

              <div className="border-t border-[#232736] pt-3 space-y-2">
                <span className="text-[10px] font-mono uppercase text-[#8b92a5]">
                  Sections Compiled
                </span>
                <div className="space-y-2 font-mono text-xs text-zinc-300">
                  {compiledRunbook.sections.map((s, idx) => (
                    <div key={idx} className="p-2.5 rounded bg-[#181b26] border border-[#232736]">
                      <div className="text-white font-bold">{s.heading}</div>
                      <div className="text-[#8b92a5] text-[11px] mt-1 whitespace-pre-line">
                        {s.body}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
